import {
  addYears,
  formatISO,
  isBefore,
  isLeapYear,
  parseISO,
  startOfToday,
  startOfYear,
} from 'date-fns';

import { getDisplayName, getOrdinalSuffix } from './profile';

export interface CalendarEvent {
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  type: string;
  classNames: string[];
  name?: string;
}

export interface Employee {
  id?: string;
  first_name: string;
  last_name: string;
  first_hire_date?: string;
  birth_date?: string;
}

export const calculateUpcomingAnniversaries = (
  employees: Employee[],
  showAllYears: boolean = false
): CalendarEvent[] => {
  const today = startOfToday();
  const startOfThisYear = startOfYear(today);
  const oneYearFromToday = addYears(today, 1);

  return employees
    .filter((employee) => employee.first_hire_date)
    .flatMap((employee) => {
      const hireDate = parseISO(employee.first_hire_date!);
      const events: CalendarEvent[] = [];

      // When showAllYears is true (calendar view), show anniversaries from start of current year
      // Otherwise (card view), show only from today forward
      const startDate = showAllYears ? startOfThisYear : today;

      // Calculate this year's anniversary
      const thisYearCount = today.getFullYear() - hireDate.getFullYear();
      const thisYearAnniversary = addYears(hireDate, thisYearCount);

      // Include this year's anniversary if it's after our start date and is a valid anniversary
      if (thisYearCount > 0 && !isBefore(thisYearAnniversary, startDate)) {
        const shouldInclude =
          showAllYears || thisYearAnniversary <= oneYearFromToday;

        if (shouldInclude) {
          events.push({
            title: `${getDisplayName(
              employee
            )}'s ${thisYearCount}${getOrdinalSuffix(
              thisYearCount
            )} Anniversary`,
            start: formatISO(thisYearAnniversary),
            end: formatISO(thisYearAnniversary),
            allDay: true,
            type: 'anniversary',
            classNames: ['anniversary-event'],
          });
        }
      }

      // Calculate next year's anniversary
      const nextYearCount = thisYearCount + 1;
      const nextYearAnniversary = addYears(hireDate, nextYearCount);

      // Include next year's anniversary if appropriate and is a valid anniversary
      const shouldIncludeNextYear =
        (showAllYears || nextYearAnniversary <= oneYearFromToday) &&
        nextYearCount > 0;

      if (shouldIncludeNextYear) {
        events.push({
          title: `${getDisplayName(
            employee
          )}'s ${nextYearCount}${getOrdinalSuffix(nextYearCount)} Anniversary`,
          start: formatISO(nextYearAnniversary),
          end: formatISO(nextYearAnniversary),
          allDay: true,
          type: 'anniversary',
          classNames: ['anniversary-event'],
        });
      }

      return events;
    })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
};

/**
 * Builds birthday calendar events. Emits exactly one event per visible employee.
 *
 * - `showAllYears` true (full calendar grid): the birthday is placed on its real
 *   date in the current year, so a birthday that has already passed this year
 *   still appears on the calendar for the remainder of the year.
 * - `showAllYears` false (e.g. the "upcoming birthday" card): the next upcoming
 *   occurrence is returned — this year's birthday if it hasn't passed yet,
 *   otherwise next year's.
 *
 * Respects the per-employee `show_calendar_birthdays` permission: an employee is
 * hidden only if they have an explicit permission entry set to false. Employees
 * without an entry default to visible.
 */
export const calculateUpcomingBirthdays = (
  employees: Employee[],
  birthdayPermissions: BirthdayPermission[] | null | undefined,
  showAllYears: boolean = false
): CalendarEvent[] => {
  const today = startOfToday();
  const currentYear = today.getFullYear();

  return employees
    .filter((employee) => {
      if (!employee.birth_date) return false;

      const hasEntry = birthdayPermissions?.some(
        (permission) => permission.employee_id === employee.id
      );
      // Default to visible when the employee has no explicit permission entry.
      if (!hasEntry) return true;

      return birthdayPermissions!.some(
        (permission) =>
          permission.employee_id === employee.id &&
          permission.show_calendar_birthdays
      );
    })
    .map((employee) => {
      const birthDate = parseISO(employee.birth_date!);
      const fullName = getDisplayName(employee);

      const buildBirthdayForYear = (year: number): Date => {
        // Feb 29 birthdays fall back to Feb 28 in non-leap years.
        const day =
          birthDate.getMonth() === 1 &&
          birthDate.getDate() === 29 &&
          !isLeapYear(new Date(year, 1, 29))
            ? 28
            : birthDate.getDate();
        return new Date(year, birthDate.getMonth(), day);
      };

      const thisYearBirthday = buildBirthdayForYear(currentYear);

      // Calendar grid: keep the birthday on its real current-year date.
      // Upcoming card: roll to next year only once this year's has passed.
      const eventDate =
        showAllYears || !isBefore(thisYearBirthday, today)
          ? thisYearBirthday
          : buildBirthdayForYear(currentYear + 1);

      return {
        title: `${fullName}'s Birthday`,
        start: formatISO(eventDate),
        end: formatISO(eventDate),
        allDay: true,
        type: 'birthday',
        name: fullName,
        classNames: ['birthday-event'],
      };
    })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
};
